import { useEffect, useRef, useState } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { ChevronDown, Menu, Moon, Sun, X } from 'lucide-react'
import { useLang } from '@/i18n/useLang'
import { useLocalized } from '@/i18n/useLocalized'
import { LangToggle } from '@/i18n/LangToggle'
import toast from 'react-hot-toast'
import { applyTheme, readTheme, type Theme } from '@/app/theme'
import { useSession, useSessionProbe } from '@/features/auth/session'
import { takeFarewell } from '@/features/auth/sign-out'
import { useHomeRoute } from '@/features/auth/home-route'
import { WORKSPACES } from '@/shared/ui/workspaces'

export function Navbar() {
  const { t, lang } = useLang()
  const L = useLocalized()
  const [open, setOpen] = useState(false)
  const bar = useRef<HTMLElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const toggle = useRef<HTMLButtonElement>(null)
  const [barBottom, setBarBottom] = useState(64)
  const [theme, setTheme] = useState<Theme>(readTheme)
  const path = useRouterState().location.pathname
  const [renderedPath, setRenderedPath] = useState(path)
  if (path !== renderedPath) {
    setRenderedPath(path)
    setOpen(false)
  }
  const signedIn = useSessionProbe() === 'authenticated'
  const account = useSession((session) => session.user)
  const pro = account?.pro != null
  // «Кабинет» и логотип вошедшего ведут туда же, куда корень: по роли и
  // активности (auth/home-route.ts); до ответа — прежний /cabinet.
  const appHome = useHomeRoute(signedIn ? account : null) ?? '/cabinet'
  const home = signedIn ? appHome : L('/')
  // Вошедшему регистрация не нужна: на месте «Начать» — его собственная зона.
  // Психологу — практика, остальным — встречи: «Мои встречи» у психолога
  // читались бы как его сессии с клиентами, а они живут в практике.
  const zone = WORKSPACES[pro ? 'practice' : 'meetings']

  // Выход и удаление аккаунта уводят сюда перезагрузкой (auth/sign-out.ts);
  // строка о том, что случилось, — одна и уже здесь. Пометка стирается при
  // чтении, поэтому смена языка второй строки не покажет.
  useEffect(() => {
    const farewell = takeFarewell()
    if (!farewell) return
    const ru = lang === 'ru'
    toast.success(
      farewell === 'deleted'
        ? ru ? 'Аккаунт удалён' : 'Your account has been deleted'
        : ru ? 'Вы вышли из аккаунта' : 'You have signed out',
    )
  }, [lang])
  const primary = [
    { href: '/courses', label: t.nav.courses },
    { href: '/books', label: t.nav.books },
    { href: '/app', label: t.nav.app },
    { href: '/practice', label: lang === 'ru' ? 'Практика' : 'Practice' },
    { href: '/certification', label: t.nav.certification },
  ]
  const more = [
    { href: '/find-psychologist', label: t.nav.findPsychologist },
    { href: '/classification', label: t.nav.classification },
    { href: '/alphabet', label: t.nav.alphabet },
    { href: '/about', label: t.nav.about },
    { href: '/contact', label: t.contact.title },
  ]

  useEffect(() => {
    if (!open) return
    const priorOverflow = document.body.style.overflow
    const opener = toggle.current
    document.body.style.overflow = 'hidden'
    panel.current?.querySelector<HTMLElement>('button, a')?.focus()
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
      if (event.key !== 'Tab') return
      const items = [...(panel.current?.querySelectorAll<HTMLElement>('a[href], button') ?? [])]
      const first = items[0]
      const last = items.at(-1)
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
    }
    document.addEventListener('keydown', handleKey)
    return () => {
      document.body.style.overflow = priorOverflow
      document.removeEventListener('keydown', handleKey)
      opener?.focus()
    }
  }, [open])

  const navLink = (link: { href: string; label: string }) => (
    <Link key={link.href} to={L(link.href)} className="nav-link" aria-current={path === L(link.href) ? 'page' : undefined}>
      {link.label}
    </Link>
  )

  return (
    <>
      <nav ref={bar} className="site-nav" aria-label={lang === 'ru' ? 'Основное меню' : 'Main navigation'}>
        <div className="container site-nav-inner">
          {/* Имя площадки, а не «На главную»: вошедшего логотип ведёт в
              приложение, и подпись «главная» соврала бы. */}
          <Link to={home} className="site-brand" aria-label="HUB4PSY">
            <img src="/logo-ocm-kpm.png" alt="" width={20} height={40} />
            <span>HUB4PSY<span className="brand-dot">.</span></span>
          </Link>
          <div className="site-nav-links">
            {primary.map(navLink)}
            <details className="nav-more" key={path}>
              <summary>{lang === 'ru' ? 'Ещё' : 'Explore'} <ChevronDown size={12} /></summary>
              <div className="nav-more-panel">{more.map(navLink)}</div>
            </details>
          </div>
          <div className="site-nav-actions">
            <LangToggle />
            <Link to={signedIn ? appHome : '/cabinet'} className="nav-account">{t.nav.cabinet}</Link>
            {signedIn ? (
              <Link to={zone.path} className="btn btn-primary btn-sm nav-start">{zone.label[lang]}</Link>
            ) : (
              <Link to="/register" className="btn btn-primary btn-sm nav-start">{t.nav.getStarted}</Link>
            )}
            <button data-theme-toggle onClick={() => {
              const next = theme === 'dark' ? 'light' : 'dark'
              setTheme(next)
              applyTheme(next)
            }} className="nav-icon" aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
              {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
            </button>
            <button ref={toggle} className="nav-icon nav-mobile-toggle" aria-label="Toggle menu" aria-expanded={open} aria-controls="site-menu" onClick={() => {
              setBarBottom(bar.current?.getBoundingClientRect().bottom ?? 64)
              setOpen(!open)
            }}>
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </nav>
      {open && (
        <div className="nav-scrim" style={{ top: barBottom }} onClick={() => setOpen(false)}>
          <div ref={panel} id="site-menu" className="nav-mobile-panel" role="dialog" aria-modal="true" aria-label={lang === 'ru' ? 'Меню сайта' : 'Site menu'} onClick={(event) => event.stopPropagation()}>
            <div className="nav-mobile-head"><span>HUB4PSY</span><button className="nav-icon" onClick={() => setOpen(false)} aria-label={lang === 'ru' ? 'Закрыть меню' : 'Close menu'}><X size={22} /></button></div>
            <div className="nav-mobile-links">
              {[...primary, ...more].map(navLink)}
              {/* Мимо L(): у кабинета нет русского адреса, и `/ru/cabinet`
                  был голой 404 — язык кабинета живёт в браузере, а не в пути. */}
              <Link to={signedIn ? appHome : '/cabinet'} className="nav-link">{t.nav.cabinet}</Link>
            </div>
            {signedIn ? (
              <Link to={zone.path} className="btn btn-primary btn-lg">{zone.label[lang]}</Link>
            ) : (
              <Link to="/register" className="btn btn-primary btn-lg">{lang === 'ru' ? 'Создать аккаунт' : 'Create an account'}</Link>
            )}
          </div>
        </div>
      )}
    </>
  )
}
